import { ErrorMessage } from '@hookform/error-message';
import type {
  DataTableColumn,
  DataTableColumnAddRequest,
  DataTableColumnApiId,
} from '@/components/UI/DataTable/DataTable.model';
import { DateTimePicker, Select } from '@/components/UI/FormFields/Latest';
import { NumberField } from '@/components/UI/FormFields/Latest/Number';
import Link from '@/components/UI/Link';
import { _isEmpty, _isNil, _isNotBlank } from '@/littledash';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form@latest';
import { DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { uniqueNameValidatorFactory } from '@/components/UI/DataTable/toolbar/Menus/AddColumn/AddColumn.util';
import styles from './EditDataTableTimestampBaselineColumn.module.scss';
import { RouteService } from '@/support/RouteService';
import { TimepointInput } from '@/components/UI/Timepoint/TimepointInput';
import Switch from '@/components/UI/FormElements/Switch';

interface EditDataTableTimestampBaselineColumnForm {
  name: string;
  from: string;
  interval: number;
  interval_time_unit: 'MINUTES' | 'HOURS' | 'DAYS';
  clash_window: string | null;
}

interface EditDataTableTimestampBaselineColumnProps {
  formData: EditDataTableTimestampBaselineColumnForm;
  columnId?: DataTableColumnApiId;
  columns: Array<DataTableColumn>;
  readOnly: boolean;
  onSubmit: (data: DataTableColumnAddRequest) => Promise<void>;
  closeModal: () => void;
}
export const timeUnitOptions = [
  {
    label: 'Minutes',
    value: 'MINUTES',
  },
  {
    label: 'Hours',
    value: 'HOURS',
  },
  {
    label: 'Days',
    value: 'DAYS',
  },
];

export const EditDataTableTimestampBaselineColumn: FC<EditDataTableTimestampBaselineColumnProps> = ({
  formData,
  columnId,
  columns,
  readOnly,
  onSubmit,
  closeModal,
}) => {
  const uniqueColumnName = useMemo(
    () =>
      uniqueNameValidatorFactory(
        new Set(columns.filter((c) => c.id !== columnId).map(({ name }) => (name ?? '').trim()))
      ),
    [columnId, columns]
  );

  const { interval, interval_time_unit } = formData;

  const methods = useForm<EditDataTableTimestampBaselineColumnForm>({
    mode: 'onChange',
    defaultValues: {
      ...formData,
      interval: DateUtils.convertSecondsToUnit(interval, interval_time_unit),
      interval_time_unit: interval_time_unit ?? 'MINUTES',
    },
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = methods;

  const mapFormData = async (data: EditDataTableTimestampBaselineColumnForm) => {
    const { name, from, interval, interval_time_unit, clash_window } = data;

    await onSubmit({
      type: 'timestampBaseline',
      name,
      pk: {
        from,
        interval: DateUtils.convertUnitToSeconds(interval, interval_time_unit),
        interval_time_unit,
        clash_window,
      },
    });
    closeModal();
  };

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title={`${_isNil(columnId) ? 'Add' : 'Update'} Timestamp: PK Dose`}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={
          <>
            Pick a start time and specify intervals.{' '}
            <Link to={RouteService.help('timestamp_baseline_columns')} openTab style={{ color: 'dodgerblue' }}>
              Read more
            </Link>
          </>
        }
      />
      <FormProvider {...methods}>
        <form>
          <div className={styles['data-table-formula-column-container']}>
            <div className="pa3 w-100 bl b--moon-gray h-100">
              <div style={{ flexGrow: 4 }}>
                <label htmlFor="name">Column name</label>
                <input
                  type="text"
                  {...register('name', {
                    required: 'This field is required',
                    maxLength: 255,
                    validate: { uniqueColumnName },
                  })}
                  placeholder="Name"
                  className={errors?.name ? 'input__error' : ''}
                  style={{ marginBottom: 0 }}
                  autoFocus
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => <small className="red db pt2">{message}</small>}
                />
              </div>
              <div className="mt3">
                <DateTimePicker name="from" label="Start time" includeTime={true} disabled={readOnly} />
                <ErrorMessage
                  errors={errors}
                  name="from"
                  render={({ message }) => <small className="red db pt2">{message}</small>}
                />
              </div>
              <div className="mt3">
                <div className="flex justify-apart">
                  <NumberField
                    name="interval"
                    label="Time between doses"
                    required
                    min={1}
                    disabled={readOnly}
                    autoFocus={true}
                  />
                  <div className="pl2">
                    <Select
                      required
                      name="interval_time_unit"
                      label="Unit"
                      options={timeUnitOptions}
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
              <div className="mt3">
                <Controller
                  control={control}
                  name="clash_window"
                  rules={{ validate: DateUtils.validateClashWindow }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { invalid } }) => {
                    const isEnabled = _isNotBlank(value);
                    return (
                      <div className="flex flex-column">
                        <div className="flex flex-row justify-between items-center">
                          <label>Clash window</label>
                          <Switch
                            value={isEnabled}
                            onChange={(enabled: boolean) => onChange(enabled ? 'PT3M' : null)}
                          />
                        </div>
                        {isEnabled ? (
                          <>
                            <label className="mt2 db">Warn me when timestamps are within:</label>
                            <div className="mt2">
                              <TimepointInput
                                value={value}
                                onChange={(change) => {
                                  onChange(_isNotBlank(change.value) ? change.value : null);
                                }}
                                defaultUnit="minutes"
                                onBlur={onBlur}
                                invalid={invalid}
                              />
                            </div>
                            {invalid ? <p className="f6 red db pv1">{errors?.clash_window?.message}</p> : null}
                          </>
                        ) : null}
                      </div>
                    );
                  }}
                />
              </div>
            </div>
            <ModalActions
              className="pa3 bt b--moon-gray"
              onSubmit={handleSubmit(mapFormData)}
              onCancel={closeModal}
              submitBtnText={_isNil(columnId) ? 'Add' : 'Update'}
              submitButtonProps={{ disabled: !_isEmpty(errors) || !isDirty || isSubmitting, submit: true }}
            />
          </div>
        </form>
      </FormProvider>
    </ModalContainer>
  );
};
